// src/components/auction/AuctionListoneLegaTab.js
// Tab "Lega" delle aste a listone. L'utente carica/incolla l'export "Esporta
// rose" della sua lega di Fantacalcio.it e ottiene KPI di lega + la propria
// rosa incrociata con le rose della lega (con grafici) + alcune classifiche.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './AuctionListoneLegaTab.css';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid,
  ReferenceLine, ReferenceArea, Tooltip as RTooltip, Cell, PieChart, Pie,
} from 'recharts';
import {
  Upload, ClipboardPaste, Info, Users, ChevronDown, AlertTriangle, CheckCircle2, X,
} from 'lucide-react';
import TeamBadge from '../TeamBadge';
import { useTheme } from '../../context/ThemeContext';
import { fmt } from './auctionCommon';
import { MANTRA_ROLE_THEME } from './MantraRoleConfiguration';
import { splitRoleTokens } from './auctionRosterUtils';
import { normalizeForMatch, getAutoMatchId, getListoneEntryById } from '../../utils/legheFantacalcioExport';
import {
  parseRostersText,
  buildOwnershipReport,
  buildLeagueStats,
  buildLeagueNameIndex,
  findLeagueCandidates,
  findUnresolvedExcelEntries,
  resolveMatchTargets,
  roleBucketOf,
  buildAutoLinks,
} from '../../utils/fantacalcioLeagueRosters';
import {
  readLeagueRosters,
  writeLeagueRosters,
  clearLeagueRosters,
  readPlayerLinks,
  writePlayerLinks,
  clearPlayerLinks,
  subscribeLeagueRosters,
} from '../../utils/leagueRostersStore';

const BUCKET_COLOR = { P: '#f5c542', D: '#22c55e', C: '#3b82f6', A: '#ef4444' };
const BAND_META = [
  { key: 'excl', label: 'Solo tuoi', color: '#22c55e', test: (r, tot) => r.count === 1 },
  { key: 'rare', label: 'Rari (≤25%)', color: '#4ade80', test: (r, tot) => r.count > 1 && r.count / tot <= 0.25 },
  { key: 'mid', label: 'Medi (26–50%)', color: '#f5c542', test: (r, tot) => r.count / tot > 0.25 && r.count / tot <= 0.5 },
  { key: 'wide', label: 'Diffusi (51–74%)', color: '#fb923c', test: (r, tot) => r.count / tot > 0.5 && r.count / tot < 0.75 },
  { key: 'ubi', label: 'Big contesi (≥75%)', color: '#ef4444', test: (r, tot) => r.count / tot >= 0.75 },
];


function rosterToPlayers(roster = []) {
  return (roster || [])
    .filter((p) => !p?.deleted)
    .map((p) => {
      const snap = p.playerSnapshot || {};
      return {
        id: String(p.playerId),
        name: snap.name || p.playerName || '',
        team: snap.team || p.playerTeam || '',
        role: snap.role || p.role || 'A',
        credits: Number(p.credits || 0),
      };
    })
    .filter((p) => p.name);
}

// Ruolo: classic → pallino P/D/C/A · mantra → token colorati (Dd, Ds, E…)
function LeagueRole({ role, rolesMode }) {
  if (rolesMode === 'mantra') {
    const tokens = splitRoleTokens(role);
    if (tokens.length) {
      return (
        <span className="lg-mtokens">
          {tokens.map((t) => {
            const th = MANTRA_ROLE_THEME[t] || MANTRA_ROLE_THEME.A;
            return (
              <span key={t} className="lg-mtoken" style={{ background: th.bg, border: `1px solid ${th.border}`, color: th.color }}>{t}</span>
            );
          })}
        </span>
      );
    }
  }
  const b = roleBucketOf(role);
  return <span className={`lg-roledot lg-roledot--${b}`}>{b}</span>;
}

function Bar({ pct, tone = 'primary' }) {
  return (
    <span className="lg-bar">
      <i className={`lg-bar__fill lg-bar__fill--${tone}`} style={{ width: `${Math.max(3, Math.round((pct || 0) * 100))}%` }} />
    </span>
  );
}

const CMP_BUCKET_LABEL = { P: 'Portieri', D: 'Difensori', C: 'Centrocampisti', A: 'Attaccanti' };

function RosterColumn({ entries, commonNorms, rolesMode }) {
  const groups = useMemo(() => {
    const g = { P: [], D: [], C: [], A: [] };
    entries.forEach((e) => g[roleBucketOf(e.role || 'A')].push(e));
    Object.values(g).forEach((arr) => arr.sort((a, b) => (b.value || b.cost || 0) - (a.value || a.cost || 0)));
    return g;
  }, [entries]);

  return (
    <div className="lg-vs__list">
      {['P', 'D', 'C', 'A'].map((b) => (
        groups[b].length ? (
          <div key={b} className="lg-vs__group">
            <div className="lg-vs__groupHead">{CMP_BUCKET_LABEL[b]}</div>
            {groups[b].map((e, i) => (
              <div key={`${e.name}-${i}`} className={`lg-vs__row ${commonNorms.has(normalizeForMatch(e.name)) ? 'is-common' : ''}`}>
                <LeagueRole role={e.role} rolesMode={rolesMode} />
                <strong>{e.name}</strong>
                <span>{e.fv > 0 ? `fv ${fmt(e.fv)}` : (e.cost ? `${e.cost} cr` : '')}</span>
              </div>
            ))}
          </div>
        ) : null
      ))}
      {!entries.length && <div className="lg-empty">—</div>}
    </div>
  );
}

// ─── Grafico: diffusione vs Δ prezzo ──────────────────────────────────────
function ScatterTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="lg-charttip">
      <strong>{d.name}</strong>
      <span>{d.count}/{d.tot} squadre lo hanno ({Math.round(d.x)}%)</span>
      <span>{d.y > 0 ? `+${fmt(d.y)} cr sopra la media` : d.y < 0 ? `${fmt(d.y)} cr sotto la media` : 'in linea con la media'}</span>
    </div>
  );
}

function DiffusionScatter({ rows, totalTeams, rolesMode }) {
  const { isDark } = useTheme();
  const grid = isDark ? 'rgba(255,255,255,.12)' : 'rgba(17,24,39,.12)';
  const axis = isDark ? '#94a3b8' : '#475569';

  const data = useMemo(() => rows
    .filter((r) => r.matched && r.avgCost != null)
    .map((r) => ({
      name: r.name,
      x: (r.count / totalTeams) * 100,
      y: r.credits - r.avgCost,
      z: Math.max(20, r.credits),
      count: r.count,
      tot: totalTeams,
      fill: BUCKET_COLOR[roleBucketOf(r.role)] || BUCKET_COLOR.A,
    })), [rows, totalTeams]);

  const bound = useMemo(() => Math.max(3, ...data.map((d) => Math.abs(d.y))), [data]);
  const roles = useMemo(() => [...new Set(data.map((d) => roleBucketOfColor(d.fill)))], [data]);

  if (data.length < 2) return null;

  return (
    <div className="lg-chart">
      <div className="lg-chart__head">
        <strong>Radiografia dei tuoi acquisti</strong>
        <span>Ogni punto è un tuo giocatore: quanto è raro nella lega (→) e se l'hai pagato più o meno della media (↑ = di più).</span>
      </div>

      <div className="lg-scatter">
        <span className="lg-scatter__q lg-scatter__q--tl">raro, ma pagato caro</span>
        <span className="lg-scatter__q lg-scatter__q--tr">comune e strapagato</span>
        <span className="lg-scatter__q lg-scatter__q--bl">colpo: raro e a sconto</span>
        <span className="lg-scatter__q lg-scatter__q--br">comune e a sconto</span>

        <ResponsiveContainer width="100%" height={240}>
          <ScatterChart margin={{ top: 10, right: 14, bottom: 22, left: 4 }}>
            <ReferenceArea x1={0} x2={50} y1={0} y2={bound} fill="#f59e0b" fillOpacity={isDark ? 0.07 : 0.06} />
            <ReferenceArea x1={50} x2={100} y1={0} y2={bound} fill="#ef4444" fillOpacity={isDark ? 0.09 : 0.07} />
            <ReferenceArea x1={0} x2={50} y1={-bound} y2={0} fill="#22c55e" fillOpacity={isDark ? 0.09 : 0.07} />
            <ReferenceArea x1={50} x2={100} y1={-bound} y2={0} fill="#3b82f6" fillOpacity={isDark ? 0.06 : 0.05} />
            <CartesianGrid stroke={grid} strokeDasharray="3 3" />
            <XAxis
              type="number" dataKey="x" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} unit="%"
              tick={{ fill: axis, fontSize: 10 }} tickLine={false}
              label={{ value: '← raro          diffuso nella lega          comune →', position: 'bottom', offset: 6, fill: axis, fontSize: 10 }}
            />
            <YAxis
              type="number" dataKey="y" domain={[-bound, bound]}
              tick={{ fill: axis, fontSize: 10 }} tickLine={false} width={34}
              label={{ value: 'cr vs media', angle: -90, position: 'insideLeft', fill: axis, fontSize: 10, dy: 30 }}
            />
            <ZAxis type="number" dataKey="z" range={[50, 300]} />
            <ReferenceLine x={50} stroke={grid} />
            <ReferenceLine y={0} stroke={axis} strokeOpacity={0.6} />
            <RTooltip content={<ScatterTip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={data}>
              {data.map((d, i) => <Cell key={i} fill={d.fill} fillOpacity={0.85} stroke={isDark ? '#0b1020' : '#fff'} strokeWidth={1} />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div className="lg-chart__legend">
        {['P', 'D', 'C', 'A'].filter((b) => roles.includes(b)).map((b) => (
          <span key={b}><i style={{ background: BUCKET_COLOR[b] }} />{{ P: 'Portieri', D: 'Difensori', C: 'Centrocampisti', A: 'Attaccanti' }[b]}</span>
        ))}
        <span className="lg-chart__legend-note">dimensione = crediti spesi</span>
      </div>
    </div>
  );
}

function roleBucketOfColor(color) {
  return Object.keys(BUCKET_COLOR).find((k) => BUCKET_COLOR[k] === color) || 'A';
}

function DiffusionDonut({ rows, totalTeams }) {
  const { isDark } = useTheme();
  const matched = rows.filter((r) => r.matched);

  const data = useMemo(() => BAND_META.map((b) => ({
    key: b.key,
    label: b.label,
    color: b.color,
    value: matched.filter((r) => b.test(r, totalTeams)).length,
  })).filter((d) => d.value > 0), [matched, totalTeams]);

  if (!data.length) return null;

  return (
    <div className="lg-chart">
      <div className="lg-chart__head"><strong>Come sono distribuiti i tuoi</strong></div>
      <div className="lg-donut">
        <ResponsiveContainer width="100%" height={190}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2} stroke={isDark ? '#0b1020' : '#fff'}>
              {data.map((d) => <Cell key={d.key} fill={d.color} />)}
            </Pie>
            <RTooltip content={({ active, payload }) => active && payload?.length
              ? <div className="lg-charttip"><strong>{payload[0].payload.label}</strong><span>{payload[0].value} giocatori</span></div>
              : null} />
          </PieChart>
        </ResponsiveContainer>
        <ul className="lg-donut__legend">
          {data.map((d) => (
            <li key={d.key}><i style={{ background: d.color }} />{d.label}<b>{d.value}</b></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─── Modale generica: elenco giocatori ───────────────────────────────────
function PlayersModal({ eyebrow, title, subtitle, players, rolesMode, onClose }) {
  return (
    <div className="lg-modal-backdrop" onClick={onClose}>
      <div className="lg-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="lg-modal__head">
          <div>
            <span>{eyebrow}</span>
            <strong>{title}</strong>
          </div>
          <button type="button" onClick={onClose} aria-label="Chiudi"><X size={16} /></button>
        </div>
        {subtitle && <p className="lg-modal__sub">{subtitle}</p>}
        <div className="lg-modal__list">
          {players.map((p, i) => (
            <div key={`${p.name}-${i}`} className="lg-modal__row">
              <LeagueRole role={p.role} rolesMode={rolesMode} />
              <strong>{p.name}</strong>
              {p.cost != null && <span>{p.cost} cr</span>}
            </div>
          ))}
          {!players.length && <div className="lg-empty">Nessun giocatore.</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Form: abbina i nomi dell'export non riconosciuti ──────────────────────
// L'export usa nomi tipo "Paz N.", "Ederson D.S." che a volte non si legano da
// soli a un giocatore del dataset. Qui l'utente dice a chi corrispondono, così
// ruoli/statistiche/possesso vengono calcolati bene.
function MatchOverlay({ entries, playerOptions, initial, onCancel, onSave, onForget }) {
  const optByLabel = useMemo(() => {
    const m = new Map();
    playerOptions.forEach((p) => m.set(`${p.name} — ${p.team}`, String(p.id)));
    return m;
  }, [playerOptions]);
  const labelById = useMemo(() => {
    const m = new Map();
    playerOptions.forEach((p) => m.set(String(p.id), `${p.name} — ${p.team}`));
    return m;
  }, [playerOptions]);

  const [draft, setDraft] = useState(() => {
    const d = {};
    entries.forEach((e) => {
      d[e.norm] = Object.prototype.hasOwnProperty.call(initial, e.norm)
        ? (initial[e.norm] == null ? '__ignore__' : String(initial[e.norm]))
        : '';
    });
    return d;
  });
  const [typed, setTyped] = useState(() => {
    const t = {};
    entries.forEach((e) => {
      const id = initial[e.norm];
      t[e.norm] = id != null && id !== undefined && labelById.get(String(id)) ? labelById.get(String(id)) : '';
    });
    return t;
  });

  const setType = (norm, value) => {
    setTyped((t) => ({ ...t, [norm]: value }));
    const id = optByLabel.get(value.trim());
    if (id) setDraft((d) => ({ ...d, [norm]: id }));
  };
  const commit = (norm) => {
    const v = (typed[norm] || '').trim().toLowerCase();
    if (!v) { setDraft((d) => ({ ...d, [norm]: d[norm] === '__ignore__' ? '__ignore__' : '' })); return; }
    const hit = [...optByLabel.entries()].find(([label]) => label.toLowerCase() === v)
      || [...optByLabel.entries()].find(([label]) => label.toLowerCase().includes(v));
    if (hit) { setDraft((d) => ({ ...d, [norm]: hit[1] })); setTyped((t) => ({ ...t, [norm]: hit[0] })); }
  };
  const ignore = (norm) => {
    setDraft((d) => ({ ...d, [norm]: '__ignore__' }));
    setTyped((t) => ({ ...t, [norm]: '' }));
  };

  const save = () => {
    const out = {};
    entries.forEach((e) => {
      const v = draft[e.norm];
      if (v === '') return;
      out[e.norm] = v === '__ignore__' ? null : v;
    });
    onSave(out);
  };

  const todo = entries.filter((e) => draft[e.norm] === '').length;

  return (
    <div className="lg-modal-backdrop" onClick={onCancel}>
      <div className="lg-modal lg-modal--wide glass" onClick={(e) => e.stopPropagation()}>
        <div className="lg-modal__head">
          <div>
            <span>Nomi dell'export da riconoscere</span>
            <strong>{todo || 'Nessuno'} da abbinare</strong>
          </div>
          <button type="button" onClick={onCancel} aria-label="Chiudi"><X size={16} /></button>
        </div>
        <p className="lg-modal__sub">
          Questi nomi dell'export non li ho collegati a nessun giocatore. Dimmi a chi
          corrispondono (o "non mi interessa"). Se è un tuo giocatore, verrà riconosciuto anche in rosa.
        </p>

        <datalist id="lg-player-opts">
          {playerOptions.map((p) => <option key={p.id} value={`${p.name} — ${p.team}`} />)}
        </datalist>

        <div className="lg-modal__list">
          {entries.map((e) => (
            <div key={e.norm} className={`lg-match-row lg-match-row--rev ${draft[e.norm] === '' ? 'is-todo' : ''}`}>
              <div className="lg-match-row__p">
                <strong>{e.name}</strong>
                <em>{e.count} rose</em>
                {draft[e.norm] === '' && <span className="lg-match-row__badge">?</span>}
                {draft[e.norm] === '__ignore__' && <span className="lg-match-row__badge" style={{ color: 'var(--muted)' }}>ignorato</span>}
              </div>
              <div className="lg-match-row__opts">
                <input
                  className="lg-match-search"
                  list="lg-player-opts"
                  placeholder="cerca giocatore…"
                  value={typed[e.norm] || ''}
                  onChange={(ev) => setType(e.norm, ev.target.value)}
                  onBlur={() => commit(e.norm)}
                  onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); commit(e.norm); } }}
                />
                <button
                  type="button"
                  className={`lg-match-chip lg-match-chip--none ${draft[e.norm] === '__ignore__' ? 'is-active' : ''}`}
                  onClick={() => ignore(e.norm)}
                >
                  non mi interessa
                </button>
              </div>
            </div>
          ))}
          {!entries.length && <div className="lg-empty">Tutti i nomi dell'export sono riconosciuti. 👍</div>}
        </div>

        <div className="lg-modal__actions">
          <button type="button" className="lg-btn" onClick={onForget}>Dimentica gli abbinamenti</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="lg-btn" onClick={onCancel}>Chiudi</button>
          <button type="button" className="lg-btn lg-btn--primary" onClick={save}>Salva</button>
        </div>
      </div>
    </div>
  );
}

// ─── Export ────────────────────────────────────────────────────────────────
export default function AuctionListoneLegaTab({ auction, roster = [], players = [], rolesMode = 'classic', myTeamName }) {
  const auctionId = auction?.id;
  const [parsed, setParsed] = useState(() => readLeagueRosters(auctionId));
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [playersModal, setPlayersModal] = useState(null); // { eyebrow, title, subtitle, players }
  // playerLinks: { [excelNorm]: playerId | null } — l'utente dice a che giocatore
  // del dataset corrisponde un nome dell'export non riconosciuto (null = ignora).
  const [playerLinks, setPlayerLinks] = useState(() => readPlayerLinks(auctionId));
  const [showMatch, setShowMatch] = useState(false);
  const [focusNorm, setFocusNorm] = useState(null); // voce extra da abbinare (da "i più presi che non hai")
  const [compareTeam, setCompareTeam] = useState('');
  const [lookupQuery, setLookupQuery] = useState('');
  const freshUploadRef = useRef(false); // apre il form abbinamento dopo un caricamento nuovo

  // I dati salvati arrivano da Firestore dopo il mount (vedi
  // useLeagueRostersPersistence in AuctionDetailPage): si riallinea lo stato.
  useEffect(() => {
    setParsed(readLeagueRosters(auctionId));
    setPlayerLinks(readPlayerLinks(auctionId));
    return subscribeLeagueRosters(auctionId, () => {
      setParsed(readLeagueRosters(auctionId));
      setPlayerLinks(readPlayerLinks(auctionId));
    });
  }, [auctionId]);

  const rosterPlayers = useMemo(() => rosterToPlayers(roster), [roster]);

  const myPlayerIds = useMemo(
    () => new Set((roster || []).filter((p) => !p?.deleted).map((p) => String(p.playerId))),
    [roster]
  );

  // Tutti i giocatori del dataset, per il selettore del form abbinamento.
  const playerOptions = useMemo(
    () => (players || [])
      .map((p) => ({ id: String(p.id), name: p.name || '', team: p.team || '', role: p.role || 'A' }))
      .filter((p) => p.name)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [players]
  );
  const playerById = useMemo(() => {
    const m = new Map();
    (players || []).forEach((p) => m.set(String(p.id), p));
    return m;
  }, [players]);

  // Arricchimento ruolo/valore/statistiche per nome (dai giocatori FantApp),
  // indicizzato su nome diretto e su nome canonico del listone Fantacalcio.
  const metaOf = useCallback((p) => ({
    id: String(p.id),
    role: p.role || 'A',
    value: Number(p.value || 0),
    goals: Number(p.goals || 0),
    assists: Number(p.assists || 0),
    saves: Number(p.spenalties || 0),
    fv: Number(p.fmarkavg || 0),
  }), []);

  const baseMetaByNorm = useMemo(() => {
    const m = new Map();
    (players || []).forEach((p) => {
      const meta = metaOf(p);
      const direct = normalizeForMatch(p.name);
      if (direct && !m.has(direct)) m.set(direct, meta);
      const id = getAutoMatchId(p.name, p.team);
      if (id) {
        const entry = getListoneEntryById(id);
        const canon = entry?.name ? normalizeForMatch(entry.name) : '';
        if (canon && !m.has(canon)) m.set(canon, meta);
      }
    });
    return m;
  }, [players, metaOf]);

  // Abbinamenti automatici "sicuri" per i nomi dell'export che non coincidono
  // col dataset: "Zambo Anguissa" → Anguissa, "Paz N." → Nico Paz (stessa
  // logica dell'import Excel di Simula rosa). Senza, quei giocatori restavano
  // senza ruolo e finivano tra gli attaccanti (ruolo di default 'A'). Le scelte
  // manuali dell'utente (anche "non mi interessa") hanno sempre la precedenza.
  const autoLinks = useMemo(
    () => (parsed?.teams ? buildAutoLinks({ leagueTeams: parsed.teams, players, playerLinks }) : {}),
    [parsed, players, playerLinks]
  );

  // Abbinamenti effettivi usati dai calcoli: automatici + manuali (questi vincono).
  const effectiveLinks = useMemo(() => ({ ...autoLinks, ...(playerLinks || {}) }), [autoLinks, playerLinks]);

  const metaByNorm = useMemo(() => {
    const m = new Map(baseMetaByNorm);
    // Il nome dell'export punta a un giocatore del dataset.
    Object.entries(effectiveLinks).forEach(([norm, pid]) => {
      if (pid == null) return;
      const p = playerById.get(String(pid));
      if (p) m.set(norm, metaOf(p));
    });
    return m;
  }, [baseMetaByNorm, effectiveLinks, playerById, metaOf]);

  const accept = useCallback((data, fileName) => {
    if (!(data?.order || []).length) {
      setError('Non ho trovato rose nel file. Usa l\'export "Esporta rose" di Fantacalcio.it.');
      return;
    }
    const next = { teams: data.teams, order: data.order, totals: data.totals || {}, source: fileName || 'testo incollato' };
    setParsed(next);
    setError('');
    freshUploadRef.current = true;
    writeLeagueRosters(auctionId, next);
  }, [auctionId]);

  // Toglie una sola squadra (utile quando la lega è costruita file per file).
  const removeTeam = useCallback((team) => {
    if (!parsed?.teams?.[team]) return;
    const teams = { ...parsed.teams };
    const totals = { ...(parsed.totals || {}) };
    delete teams[team];
    delete totals[team];
    const order = (parsed.order || []).filter((t) => t !== team);
    if (!order.length) { setParsed(null); clearLeagueRosters(auctionId); return; }
    accept({ teams, order, totals }, parsed.source);
    freshUploadRef.current = false; // togliere una squadra non è un nuovo import: niente form abbinamenti
  }, [parsed, accept, auctionId]);

  const onParseText = useCallback(() => {
    if (!text.trim()) return;
    try { accept(parseRostersText(text), null); setShowPaste(false); }
    catch (err) { setError('Testo non riconosciuto: ' + (err?.message || '') + '.'); }
  }, [text, accept]);

  const clear = useCallback(() => {
    setParsed(null); setError('');
    clearLeagueRosters(auctionId);
  }, [auctionId]);

  const nameIndex = useMemo(() => (parsed?.teams ? buildLeagueNameIndex(parsed.teams) : new Map()), [parsed]);

  const ownership = useMemo(() => {
    if (!parsed?.teams) return null;
    return buildOwnershipReport({ rosterPlayers, leagueTeams: parsed.teams, myTeamName, playerLinks: effectiveLinks });
  }, [parsed, rosterPlayers, myTeamName, effectiveLinks]);

  // Norm dei giocatori effettivamente in rosa (rispetta gli abbinamenti manuali).
  const myOwnedNorms = useMemo(
    () => new Set((ownership?.rows || []).flatMap((r) => r.matchedNorms || [])),
    [ownership]
  );

  // Norm dell'export già "sistemati": riconosciuti dal dataset o già decisi
  // dall'utente (link o "ignora"). Gli altri vanno chiesti.
  const resolvedNorms = useMemo(() => {
    const s = new Set(metaByNorm.keys());
    Object.keys(playerLinks || {}).forEach((n) => s.add(n));
    return s;
  }, [metaByNorm, playerLinks]);

  // Nomi dell'export non riconosciuti (in ≥2 rose) da far abbinare all'utente.
  const unresolvedExcel = useMemo(() => {
    if (!parsed?.teams) return [];
    return findUnresolvedExcelEntries({ leagueTeams: parsed.teams, resolvedNorms })
      .filter((e) => e.count >= 2);
  }, [parsed, resolvedNorms]);

  const closeMatch = useCallback(() => { setShowMatch(false); setFocusNorm(null); }, []);

  const handleSaveLinks = useCallback((chosen) => {
    const next = { ...playerLinks, ...chosen };
    setPlayerLinks(next);
    writePlayerLinks(auctionId, next);
    setShowMatch(false);
    setFocusNorm(null);
  }, [playerLinks, auctionId]);

  const forgetLinks = useCallback(() => {
    setPlayerLinks({});
    clearPlayerLinks(auctionId);
    setShowMatch(false);
    setFocusNorm(null);
  }, [auctionId]);

  const stats = useMemo(() => {
    if (!parsed?.teams) return null;
    return buildLeagueStats({ leagueTeams: parsed.teams, totals: parsed.totals, rosterPlayers, myTeamName, metaByNorm, ownedNorms: myOwnedNorms });
  }, [parsed, rosterPlayers, myTeamName, metaByNorm, myOwnedNorms]);

  // "I più presi che non hai": esclude quelli che l'utente ha già deciso
  // (link a un giocatore o "ignora").
  const visibleMissed = useMemo(
    () => (stats?.missedPopular || []).filter((o) => !(o.norm in (playerLinks || {}))),
    [stats, playerLinks]
  );

  // Voci mostrate nel form: i nomi non riconosciuti + l'eventuale voce su cui
  // l'utente ha cliccato da "i più presi che non hai" (anche se già riconosciuta).
  const matchEntries = useMemo(() => {
    const list = unresolvedExcel.map((e) => ({ norm: e.norm, name: e.name, count: e.count }));
    if (focusNorm && !list.some((e) => e.norm === focusNorm)) {
      const o = (stats?.ownership || []).find((x) => x.norm === focusNorm);
      list.unshift(o
        ? { norm: o.norm, name: o.name, count: o.count }
        : { norm: focusNorm, name: focusNorm, count: 0 });
    }
    return list;
  }, [unresolvedExcel, focusNorm, stats]);

  // Apre il form abbinamento a OGNI import via upload/incolla se ci sono nomi
  // dell'export non riconosciuti. Non si apre al mount con cache.
  const seenParsedRef = useRef(null);
  useEffect(() => {
    if (!parsed) return;
    const isNew = parsed !== seenParsedRef.current;
    const fromUpload = freshUploadRef.current;
    seenParsedRef.current = parsed;
    freshUploadRef.current = false;
    if (isNew && fromUpload && unresolvedExcel.length > 0) setShowMatch(true);
  }, [parsed, unresolvedExcel.length]);

  const ownRows = useMemo(() => {
    if (!ownership) return [];
    return [...ownership.rows].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [ownership]);

  const mySummary = useMemo(() => {
    if (!ownership) return null;
    const matched = ownership.rows.filter((r) => r.matched);
    const n = matched.length || 1;
    const avgPct = matched.reduce((s, r) => s + r.count / ownership.totalTeams, 0) / n;
    const mineExclusive = matched.filter((r) => r.count === 1).length;
    const overpaid = matched.filter((r) => r.avgCost != null).reduce((s, r) => s + (r.credits - r.avgCost), 0);
    return { avgPct, mineExclusive, overpaid, unmatched: ownership.rows.length - matched.length };
  }, [ownership]);

  // Conteggio possesso nella lega per norm (per arricchire "bonus in giro").
  const ownCountByNorm = useMemo(() => {
    const m = new Map();
    (stats?.ownership || []).forEach((o) => m.set(o.norm, o.count));
    return m;
  }, [stats]);

  // ── Giocatori con più bonus (gol/assist/rig. parati) che NON hai in rosa.
  const bonusLeaders = useMemo(() => {
    return (players || [])
      .filter((p) => !myPlayerIds.has(String(p.id)) && Number(p.caps || 0) >= 3)
      .map((p) => {
        const goals = Number(p.goals || 0);
        const assists = Number(p.assists || 0);
        const saves = Number(p.spenalties || 0);
        let leagueCount = null;
        if (ownCountByNorm.size) {
          const t = resolveMatchTargets({ name: p.name, team: p.team });
          for (const n of t) { if (ownCountByNorm.has(n)) { leagueCount = ownCountByNorm.get(n); break; } }
        }
        return {
          id: String(p.id), name: p.name, team: p.team, role: p.role,
          goals, assists, saves, leagueCount,
          bonus: goals * 3 + assists + saves * 3,
        };
      })
      .filter((p) => p.bonus > 0)
      .sort((a, b) => b.bonus - a.bonus)
      .slice(0, 15);
  }, [players, myPlayerIds, ownCountByNorm]);

  // ── Cerca un giocatore del TUO dataset: in quali squadre della lega è presente.
  const stripAccents = (s = '') => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  // playerId → [excelNorm…] dagli abbinamenti (inverso di effectiveLinks).
  const linkedNormsByPlayerId = useMemo(() => {
    const m = new Map();
    Object.entries(effectiveLinks).forEach(([norm, pid]) => {
      if (pid == null) return;
      const k = String(pid);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(norm);
    });
    return m;
  }, [effectiveLinks]);

  const lookupCandidates = useMemo(() => {
    const q = stripAccents(lookupQuery.trim());
    if (q.length < 2) return [];
    const starts = [];
    const contains = [];
    playerOptions.forEach((o) => {
      const n = stripAccents(o.name);
      if (n.startsWith(q)) starts.push(o);
      else if (n.includes(q) || stripAccents(o.team).startsWith(q)) contains.push(o);
    });
    return [...starts, ...contains].slice(0, 12);
  }, [lookupQuery, playerOptions]);

  const lookup = useMemo(() => {
    if (!parsed?.teams) return null;
    const q = stripAccents(lookupQuery.trim());
    if (q.length < 2) return null;
    // Testo digitato → giocatore del dataset: match esatto su "Nome · Squadra",
    // poi nome esatto, poi candidato unico.
    const opt = playerOptions.find((o) => stripAccents(`${o.name} · ${o.team}`) === q)
      || playerOptions.find((o) => stripAccents(o.name) === q)
      || (lookupCandidates.length === 1 ? lookupCandidates[0] : null);
    if (!opt) return null;

    const myNorm = normalizeForMatch(myTeamName || '');
    const role = playerById.get(String(opt.id))?.role || opt.role || null;
    const base = { id: opt.id, name: opt.name, team: opt.team, role };

    const ownersFor = (norms) => {
      const set = norms instanceof Set ? norms : new Set(norms);
      return Object.entries(parsed.teams)
        .map(([team, list]) => {
          const hit = (list || []).find((p) => set.has(normalizeForMatch(p.name)));
          return hit ? { team, cost: Number(hit.cost || 0), isMine: myNorm && normalizeForMatch(team) === myNorm } : null;
        })
        .filter(Boolean)
        .sort((a, b) => b.cost - a.cost);
    };

    // 1) abbinamento certo: nome diretto + bridge listone + link manuale
    const direct = new Set([
      ...resolveMatchTargets({ name: opt.name, team: opt.team }),
      ...(linkedNormsByPlayerId.get(String(opt.id)) || []),
    ]);
    const directOwners = ownersFor(direct);
    if (directOwners.length) return { ...base, owners: directOwners };

    // 2) fallback: candidati per cognome/prefisso nell'export
    const cands = findLeagueCandidates({ name: opt.name, team: opt.team }, nameIndex)
      .filter((c) => c.score >= 60);
    const options = cands
      .map((c) => ({ norm: c.norm, name: c.name, owners: ownersFor([c.norm]) }))
      .filter((x) => x.owners.length);

    if (!options.length) return { ...base, owners: [] };
    if (options.length === 1) return { ...base, owners: options[0].owners, fuzzyName: options[0].name };
    return { ...base, options };
  }, [parsed, lookupQuery, playerOptions, lookupCandidates, linkedNormsByPlayerId, playerById, nameIndex, myTeamName]);

  // ── Confronto rosa: la mia vs una squadra della lega.
  const compareData = useMemo(() => {
    if (!parsed?.teams || !compareTeam || !parsed.teams[compareTeam]) return null;

    const summarize = (entries) => {
      const byRole = { P: 0, D: 0, C: 0, A: 0 };
      let goals = 0, assists = 0, saves = 0, value = 0, spent = 0, fvSum = 0, fvN = 0;
      entries.forEach((e) => {
        byRole[roleBucketOf(e.role || 'A')] += 1;
        goals += e.goals; assists += e.assists; saves += e.saves;
        value += e.value; spent += e.cost;
        if (e.fv > 0) { fvSum += e.fv; fvN += 1; }
      });
      return { count: entries.length, byRole, goals, assists, saves, value, spent, avgFv: fvN ? fvSum / fvN : 0, entries };
    };

    const their = (parsed.teams[compareTeam] || []).map((p) => {
      const meta = metaByNorm.get(normalizeForMatch(p.name)) || {};
      return {
        // Ruolo dal dataset; se il nome non è riconosciuto, quello del file
        // (la rosa singola lo riporta, es. "Dd/E") prima del default.
        name: p.name, cost: Number(p.cost || 0), role: meta.role || p.role || 'A',
        goals: meta.goals || 0, assists: meta.assists || 0, saves: meta.saves || 0,
        fv: meta.fv || 0, value: meta.value || 0,
      };
    });
    const theirSum = summarize(their);
    theirSum.spent = Number.isFinite(Number(parsed.totals?.[compareTeam])) ? Number(parsed.totals[compareTeam]) : theirSum.spent;

    const mine = (roster || []).filter((p) => !p?.deleted).map((p) => {
      const s = p.playerSnapshot || {};
      return {
        name: s.name || p.playerName, cost: Number(p.credits || 0), role: s.role || p.role || 'A',
        goals: Number(s.goals || 0), assists: Number(s.assists || 0), saves: Number(s.spenalties || 0),
        fv: Number(s.fmarkavg || 0), value: Number(s.value || 0),
      };
    });
    const mineSum = summarize(mine);

    const theirNorms = new Set(their.map((p) => normalizeForMatch(p.name)));
    // Giocatori in comune: match sui nomi diretti + sui norm dei miei giocatori
    // già abbinati alla lega (post-override).
    const mineNorms = new Set([...mine.map((p) => normalizeForMatch(p.name)), ...myOwnedNorms]);
    const commonNorms = new Set([...mineNorms].filter((n) => theirNorms.has(n)));
    const common = [...commonNorms].map((n) => nameIndex.get(n) || n);

    return { team: compareTeam, mine: mineSum, their: theirSum, common, commonNorms };
  }, [parsed, compareTeam, metaByNorm, roster, myOwnedNorms, nameIndex]);

  return (
    <div className="lg-tab">
      {/* ── Carica / incolla ── */}
      <section className="lg-panel glass">
        <div className="lg-panel__head">
          <h3><Users size={16} /> Rose della lega Fantacalcio.it</h3>
          <span>Carica l'export delle rose per sbloccare le statistiche di lega</span>
        </div>

        <button type="button" className="lg-help-toggle" onClick={() => setShowHelp((s) => !s)}>
          <Info size={14} /> Come esportare le rose
          <ChevronDown size={14} className={showHelp ? 'is-open' : ''} />
        </button>
        {showHelp && (
          <ol className="lg-help">
            <li>Apri <code>leghe.fantacalcio.it/&lt;tua-lega&gt;/view/rosters/</code> da loggato.</li>
            <li>Usa il pulsante <strong>Esporta rose</strong> (Excel) e carica il file qui.</li>
            <li>
              Oppure carica la <strong>rosa di una singola squadra</strong> (CSV, es.{' '}
              <code>lega_squadra_rosa_data.csv</code>): ogni file si aggiunge alle squadre già caricate, puoi
              selezionarne anche più di uno insieme. Il nome della squadra è preso dal nome del file.
            </li>
            <li>Oppure seleziona la tabella (Ctrl+A) → Copia → incollala in "Incolla testo".</li>
          </ol>
        )}

        <div className="lg-actions">
          {/* Il caricamento file è nell'intestazione dell'asta, accanto a "Crediti
              iniziali": aggiorna insieme questo tab e la tua rosa nel Market. */}
          <span className="lg-upload-hint">
            <Upload size={14} /> Le rose (Excel o CSV) si caricano dal pulsante <b>Carica rose</b> in alto, accanto ai crediti iniziali.
          </span>
          <button type="button" className="lg-btn" onClick={() => setShowPaste((s) => !s)}>
            <ClipboardPaste size={15} /> Incolla testo
          </button>
        </div>

        {showPaste && (
          <div className="lg-paste">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
              placeholder="Incolla qui il contenuto copiato dalla pagina rose…" />
            <button type="button" className="lg-btn lg-btn--primary" onClick={onParseText} disabled={!text.trim()}>Analizza</button>
          </div>
        )}

        {error && <div className="lg-notice lg-notice--err"><AlertTriangle size={14} /> {error}</div>}
        {parsed && stats && (
          <div className="lg-notice lg-notice--ok">
            <CheckCircle2 size={14} />
            {stats.teamCount} squadre da "{parsed.source}"
            {stats.myTeamMatched ? ' · squadra riconosciuta' : ' · la tua squadra non è tra queste (controlla il nome)'}
            <button type="button" className="lg-clear" onClick={clear} aria-label="Rimuovi"><X size={13} /></button>
          </div>
        )}
        {parsed && (parsed.order || []).length > 0 && (
          <div className="lg-loaded-teams">
            {parsed.order.map((t) => (
              <span key={t} className="lg-loaded-team">
                {t} · {(parsed.teams[t] || []).length}
                <button type="button" onClick={() => removeTeam(t)} aria-label={`Togli ${t}`}><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
        {parsed && unresolvedExcel.length > 0 && (
          <div className="lg-notice lg-notice--warn">
            <AlertTriangle size={14} />
            <span>
              <strong>{unresolvedExcel.length} nomi dell'export</strong> non riconosciuti
              {' '}({unresolvedExcel.slice(0, 3).map((e) => e.name).join(', ')}{unresolvedExcel.length > 3 ? '…' : ''}).
              {' '}Abbinali per calcoli più precisi.
            </span>
            <button type="button" className="lg-btn lg-btn--primary lg-btn--sm" onClick={() => setShowMatch(true)}>Abbina</button>
          </div>
        )}
        {parsed && unresolvedExcel.length === 0 && Object.keys(playerLinks).length > 0 && (
          <button type="button" className="lg-match-cta" onClick={() => setShowMatch(true)}>
            Rivedi abbinamenti nomi
          </button>
        )}
      </section>

      {parsed && stats && ownership && (
        <>
          {/* ── KPI di lega ── */}
          <section className="lg-panel glass">
            <div className="lg-panel__head"><h3>Panoramica lega</h3></div>
            <div className="lg-kpis">
              <div className="lg-kpi"><span>Squadre</span><strong>{stats.teamCount}</strong></div>
              <div className="lg-kpi"><span>Giocatori / squadra</span><strong>{stats.playersPerTeam}</strong></div>
              <div className="lg-kpi"><span>Giocatori distinti</span><strong>{stats.totalDistinct}</strong></div>
              <div className="lg-kpi"><span>Crediti in gioco</span><strong>{stats.creditsInPlay}</strong></div>
              <div className="lg-kpi"><span>Spesa media</span><strong>{stats.avgSpend}</strong></div>
              <div className="lg-kpi"><span>Spesa min–max</span><strong>{stats.minSpend}–{stats.maxSpend}</strong></div>
              <div className="lg-kpi"><span>Prezzo medio / giocatore</span><strong>{fmt(stats.avgPlayerPrice)}</strong></div>
              <div className="lg-kpi"><span>Esclusivi (1 rosa)</span><strong>{stats.exclusiveCount}</strong></div>
              <div className="lg-kpi"><span>Big monopolizzati (&ge;75%)</span><strong>{stats.ubiquitousCount}</strong></div>
              <div className="lg-kpi lg-kpi--accent">
                <span>La tua spesa (export)</span>
                <strong>{stats.mySpend != null ? stats.mySpend : '—'}</strong>
              </div>
            </div>

            <div className="lg-highlights">
              {stats.mostContested && (
                <div className="lg-highlight">
                  <span>Più conteso</span><strong>{stats.mostContested.name}</strong>
                  <em>{stats.mostContested.count}/{stats.teamCount} rose</em>
                </div>
              )}
              {stats.priciestSingle && (
                <div className="lg-highlight">
                  <span>Acquisto più caro</span><strong>{stats.priciestSingle.name}</strong>
                  <em>{stats.priciestSingle.cost} cr · {stats.priciestSingle.team}</em>
                </div>
              )}
              {stats.priciestAvg && (
                <div className="lg-highlight">
                  <span>Più caro di media</span><strong>{stats.priciestAvg.name}</strong>
                  <em>{fmt(stats.priciestAvg.avgCost)} cr · {stats.priciestAvg.count} rose</em>
                </div>
              )}
              {stats.topSpender && (
                <div className="lg-highlight">
                  <span>Ha speso di più</span><strong>{stats.topSpender.team}</strong>
                  <em>{stats.topSpender.total} cr</em>
                </div>
              )}
            </div>

            {stats.spendByBucket && stats.spendByBucket.coverage >= 0.6 && (
              <div className="lg-bucketspend">
                <span className="lg-bucketspend__label">Spesa media di lega per reparto</span>
                <div className="lg-bucketspend__bars">
                  {['P', 'D', 'C', 'A'].map((b) => (
                    <div key={b} className="lg-bucketspend__row">
                      <span className={`lg-roledot lg-roledot--${b}`}>{b}</span>
                      <Bar pct={stats.spendByBucket.pct[b]} tone={b === 'P' ? 'muted' : b === 'D' ? 'teal' : b === 'C' ? 'primary' : 'accent'} />
                      <span>{Math.round(stats.spendByBucket.pct[b] * 100)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* ── Cerca un giocatore ── */}
          <section className="lg-panel glass">
            <div className="lg-panel__head">
              <h3>Cerca un giocatore</h3>
              <span>Scrivi un giocatore del dataset: ti dico in quali squadre della lega è e quanto l'hanno pagato</span>
            </div>
            <input
              className="lg-compare-select"
              list="lg-lookup-names"
              value={lookupQuery}
              onChange={(e) => setLookupQuery(e.target.value)}
              placeholder="Scrivi un nome (es. Thuram)…"
            />
            <datalist id="lg-lookup-names">
              {lookupCandidates.map((o) => (
                <option key={o.id} value={`${o.name} · ${o.team}`} />
              ))}
            </datalist>

            {lookup && lookup.options && (
              <div className="lg-lookup-card">
                <div className="lg-lookup-card__head">
                  <LeagueRole role={lookup.role} rolesMode={rolesMode} />
                  <strong>{lookup.name}</strong>
                  {lookup.team && <span>{lookup.team}</span>}
                </div>
                <p className="lg-lookup-card__note">
                  Nell'export della lega risulta con più nomi possibili — ecco dove sono:
                </p>
                {lookup.options.map((opt) => (
                  <div key={opt.norm} className="lg-lookup-card__opt">
                    <span className="lg-lookup-card__optName">«{opt.name}» · {opt.owners.length}/{stats.teamCount} rose</span>
                    <div className="lg-lookup-card__teams">
                      {opt.owners.map((o) => (
                        <span key={o.team} className={`lg-team-chip ${o.isMine ? 'is-mine' : ''}`}>
                          {o.team} · <b>{o.cost}</b>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {lookup && !lookup.options && (
              <div className="lg-lookup-card">
                <div className="lg-lookup-card__head">
                  <LeagueRole role={lookup.role} rolesMode={rolesMode} />
                  <strong>{lookup.name}</strong>
                  {lookup.team && <span>{lookup.team}</span>}
                  <span>· {lookup.owners.length}/{stats.teamCount} rose</span>
                  {lookup.owners.length > 0 && (
                    <span>· medio {fmt(lookup.owners.reduce((s, o) => s + o.cost, 0) / lookup.owners.length)} cr</span>
                  )}
                  {lookup.fuzzyName && <span className="lg-lookup-card__fuzzy">abbinato a «{lookup.fuzzyName}»</span>}
                </div>
                {lookup.owners.length ? (
                  <div className="lg-lookup-card__teams">
                    {lookup.owners.map((o) => (
                      <span key={o.team} className={`lg-team-chip ${o.isMine ? 'is-mine' : ''}`}>
                        {o.team} · <b>{o.cost}</b>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="lg-empty">Nessuna squadra della lega ha questo giocatore.</div>
                )}
              </div>
            )}

            {!lookup && lookupCandidates.length > 0 && (
              <div className="lg-lookup-hints">
                {lookupCandidates.slice(0, 8).map((o) => (
                  <button key={o.id} type="button" className="lg-match-chip" onClick={() => setLookupQuery(`${o.name} · ${o.team}`)}>
                    {o.name} <em>{o.team}</em>
                  </button>
                ))}
              </div>
            )}
            {!lookup && lookupQuery.trim().length >= 2 && lookupCandidates.length === 0 && (
              <div className="lg-empty">Nessun giocatore col nome «{lookupQuery.trim()}» nel dataset.</div>
            )}
          </section>

          {/* ── La tua rosa nella lega ── */}
          <section className="lg-panel glass">
            <div className="lg-panel__head">
              <h3>La tua rosa nella lega</h3>
              <span>Quante e quali squadre hanno ogni tuo giocatore · quanto hai pagato tu vs la media</span>
            </div>

            {mySummary && (
              <div className="lg-summary">
                <span>Diffusione media <strong>{Math.round(mySummary.avgPct * 100)}%</strong> delle squadre</span>
                <span><strong>{mySummary.mineExclusive}</strong> giocatori solo tuoi</span>
                <span className={mySummary.overpaid > 0 ? 'is-neg' : 'is-pos'}>
                  {mySummary.overpaid >= 0 ? 'Sovrapprezzo' : 'Risparmio'} vs media lega <strong>{fmt(Math.abs(mySummary.overpaid))} cr</strong>
                </span>
                {mySummary.unmatched > 0 && (
                  <span className="is-warn"><AlertTriangle size={12} /> {mySummary.unmatched} non abbinati</span>
                )}
              </div>
            )}

            <div className="lg-charts2">
              <DiffusionScatter rows={ownRows} totalTeams={ownership.totalTeams} rolesMode={rolesMode} />
              <DiffusionDonut rows={ownRows} totalTeams={ownership.totalTeams} />
            </div>

            <div className="lg-own-list">
              {ownRows.map((r) => (
                <div key={r.id} className={`lg-own-row ${!r.matched ? 'is-unmatched' : ''}`}>
                  <div className="lg-own-row__p">
                    <TeamBadge team={r.team} size={20} />
                    <LeagueRole role={r.role} rolesMode={rolesMode} />
                    <strong>{r.name}</strong>
                  </div>
                  {r.matched ? (
                    <>
                      <div className="lg-own-row__mid">
                        <span className="lg-own-row__count">{r.count}<i>/{ownership.totalTeams}</i></span>
                        {r.avgCost != null && (
                          <span className={`lg-own-row__cost ${r.credits - r.avgCost > 0 ? 'is-over' : r.credits - r.avgCost < 0 ? 'is-under' : ''}`}>
                            tu <b>{r.credits}</b> · media <b>{fmt(r.avgCost)}</b>
                          </span>
                        )}
                      </div>
                      <div className="lg-own-row__teams">
                        {r.ownerTeams.map((t) => (
                          <span key={t} className={`lg-team-chip ${t === myTeamName ? 'is-mine' : ''}`}>{t}</span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <span className="lg-own-row__na">non in nessuna rosa</span>
                  )}
                </div>
              ))}
              {!ownRows.length && <div className="lg-empty">Aggiungi giocatori nel tab Squadra.</div>}
            </div>
          </section>

          {/* ── Più presi che NON hai ── */}
          <section className="lg-panel glass">
            <div className="lg-panel__head">
              <h3>I più presi che non hai in rosa</h3>
              <span>Giocatori che tante squadre della lega hanno preso e tu no · clicca se in realtà ce l'hai (o per confermare che non è tuo)</span>
            </div>
            <div className="lg-rank">
              {visibleMissed.length ? visibleMissed.map((o, i) => (
                <button
                  key={o.norm}
                  type="button"
                  className="lg-rank__row lg-rank__row--btn"
                  onClick={() => { setFocusNorm(o.norm); setShowMatch(true); }}
                >
                  <span className="lg-rank__pos">{i + 1}</span>
                  <LeagueRole role={o.role} rolesMode={rolesMode} />
                  <strong>{o.name}</strong>
                  <Bar pct={o.pct} />
                  <span className="lg-rank__val">{o.count}/{stats.teamCount}</span>
                  <span className="lg-rank__sub">{fmt(o.avgCost)} cr</span>
                </button>
              )) : <div className="lg-empty">Hai in rosa tutti i giocatori più presi. 💪</div>}
            </div>
          </section>

          {/* ── Bonus in giro che non hai ── */}
          {bonusLeaders.length > 0 && (
            <section className="lg-panel glass">
              <div className="lg-panel__head">
                <h3>Bonus in giro che non hai in rosa</h3>
                <span>Gol · Assist · Rigori parati della stagione — giocatori che non hai</span>
              </div>
              <div className="lg-bonus">
                <div className={`lg-bonus__head ${ownCountByNorm.size > 0 ? 'has-own' : ''}`}>
                  <span />
                  <span>G</span><span>A</span><span>RP</span>
                  {ownCountByNorm.size > 0 && <span>rose</span>}
                </div>
                {bonusLeaders.map((p, i) => (
                  <div key={p.id} className={`lg-bonus__row ${ownCountByNorm.size > 0 ? 'has-own' : ''}`}>
                    <div className="lg-bonus__p">
                      <span className="lg-rank__pos">{i + 1}</span>
                      <LeagueRole role={p.role} rolesMode={rolesMode} />
                      <strong>{p.name}</strong>
                      <em>{p.team}</em>
                    </div>
                    <span className={p.goals ? 'is-hot' : ''}>{p.goals || '·'}</span>
                    <span className={p.assists ? 'is-hot' : ''}>{p.assists || '·'}</span>
                    <span className={p.saves ? 'is-hot' : ''}>{p.saves || '·'}</span>
                    {ownCountByNorm.size > 0 && <span className="lg-bonus__own">{p.leagueCount != null ? `${p.leagueCount}/${stats?.teamCount ?? '?'}` : '—'}</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Classifiche ── */}
          <div className="lg-grid2">
            <section className="lg-panel glass">
              <div className="lg-panel__head">
                <h3>Squadre più simili alla tua</h3>
                <span>Giocatori in comune con la tua rosa — clicca per vederli</span>
              </div>
              <div className="lg-rank">
                {stats.similarTeams.length ? stats.similarTeams.map((t) => (
                  <button
                    key={t.team}
                    type="button"
                    className="lg-rank__row lg-rank__row--btn"
                    onClick={() => setPlayersModal({
                      eyebrow: 'In comune con', title: t.team,
                      subtitle: `${t.shared} giocatori che hai anche tu (${Math.round(t.jaccard * 100)}% di sovrapposizione).`,
                      players: t.players,
                    })}
                  >
                    <strong>{t.team}</strong>
                    <Bar pct={t.jaccard} tone="teal" />
                    <span className="lg-rank__val">{t.shared}</span>
                    <ChevronDown size={14} className="lg-rank__chevron" />
                  </button>
                )) : <div className="lg-empty">Nessuna sovrapposizione (aggiungi giocatori alla tua rosa).</div>}
              </div>
            </section>

            <section className="lg-panel glass">
              <div className="lg-panel__head">
                <h3>Chi scommette di più</h3>
                <span>Squadre con più giocatori esclusivi — clicca per vederli</span>
              </div>
              <div className="lg-rank">
                {stats.mostExclusiveTeams.length ? stats.mostExclusiveTeams.map((t) => (
                <button
                  key={t.team}
                  type="button"
                  className={`lg-rank__row lg-rank__row--btn ${t.isMine ? 'is-mine' : ''}`}
                  onClick={() => setPlayersModal({
                    eyebrow: 'Giocatori solo di', title: t.team,
                    subtitle: `${t.n} giocatori che nessun'altra squadra della lega ha in rosa.`,
                    players: t.players,
                  })}
                >
                  <strong>{t.team}</strong>
                  <Bar pct={t.n / (stats.mostExclusiveTeams[0]?.n || 1)} tone="teal" />
                  <span className="lg-rank__val">{t.n}</span>
                  <ChevronDown size={14} className="lg-rank__chevron" />
                </button>
              )) : <div className="lg-empty">—</div>}
              </div>
            </section>
          </div>

          {/* ── Confronta con una squadra ── */}
          <section className="lg-panel glass">
            <div className="lg-panel__head">
              <h3>Confronta la tua rosa con una squadra</h3>
              <span>Scegli un avversario e vedi come vi confrontate</span>
            </div>
            <select
              className="lg-compare-select"
              value={compareTeam}
              onChange={(e) => setCompareTeam(e.target.value)}
            >
              <option value="">— scegli una squadra —</option>
              {(parsed.order || []).filter((t) => !stats.spendByTeam.find((s) => s.team === t && s.isMine)).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {compareData && (
              <>
                <div className="lg-vs">
                  <div className="lg-vs__col">
                    <div className="lg-vs__colHead lg-vs__colHead--mine">La tua rosa</div>
                    <RosterColumn entries={compareData.mine.entries} commonNorms={compareData.commonNorms} rolesMode={rolesMode} />
                  </div>
                  <div className="lg-vs__col">
                    <div className="lg-vs__colHead lg-vs__colHead--their">{compareData.team}</div>
                    <RosterColumn entries={compareData.their.entries} commonNorms={compareData.commonNorms} rolesMode={rolesMode} />
                  </div>
                </div>

                <div className="lg-compare-grid">
                  {[
                    ['Spesa', compareData.mine.spent, compareData.their.spent, false],
                    ['Giocatori', compareData.mine.count, compareData.their.count, false],
                    ['Portieri', compareData.mine.byRole.P, compareData.their.byRole.P, false],
                    ['Difensori', compareData.mine.byRole.D, compareData.their.byRole.D, false],
                    ['Centrocampisti', compareData.mine.byRole.C, compareData.their.byRole.C, false],
                    ['Attaccanti', compareData.mine.byRole.A, compareData.their.byRole.A, false],
                    ['Gol rosa', compareData.mine.goals, compareData.their.goals, true],
                    ['Assist rosa', compareData.mine.assists, compareData.their.assists, true],
                    ['Rig. parati rosa', compareData.mine.saves, compareData.their.saves, true],
                    ['Fantamedia media', fmt(compareData.mine.avgFv), fmt(compareData.their.avgFv), true],
                    ['Valore listino', fmt(compareData.mine.value), fmt(compareData.their.value), true],
                  ].map(([label, a, b, higherWins]) => {
                    const na = Number(a); const nb = Number(b);
                    const cmp = higherWins ? Math.sign(na - nb) : 0;
                    return (
                      <div key={label} className="lg-compare-row">
                        <span className={`lg-compare-row__v ${cmp > 0 ? 'is-win' : cmp < 0 ? 'is-lose' : ''}`}>{a}</span>
                        <span className="lg-compare-row__l">{label}</span>
                        <span className={`lg-compare-row__v ${cmp < 0 ? 'is-win' : cmp > 0 ? 'is-lose' : ''}`}>{b}</span>
                      </div>
                    );
                  })}
                  <div className="lg-compare-row lg-compare-row--head">
                    <span>La tua rosa</span><span /><span>{compareData.team}</span>
                  </div>
                </div>

                {compareData.common.length > 0 && (
                  <div className="lg-compare-common">
                    <span>{compareData.common.length} giocatori in comune:</span>
                    {compareData.common.map((n) => <span key={n} className="lg-team-chip is-mine">{n}</span>)}
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      {playersModal && (
        <PlayersModal
          eyebrow={playersModal.eyebrow}
          title={playersModal.title}
          subtitle={playersModal.subtitle}
          players={playersModal.players}
          rolesMode={rolesMode}
          onClose={() => setPlayersModal(null)}
        />
      )}

      {showMatch && (
        <MatchOverlay
          entries={matchEntries}
          playerOptions={playerOptions}
          initial={playerLinks}
          onCancel={closeMatch}
          onSave={handleSaveLinks}
          onForget={forgetLinks}
        />
      )}
    </div>
  );
}
